import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { motion } from "framer-motion";
import Select from "react-select";
import { FaPlus, FaUndo, FaList, FaCheck, FaTrash } from "react-icons/fa";
import {
  convertToDateInputFormat,
  formatDate,
  getTodayDate,
} from "../../config/helperFunctions";
import { handleDropDowns } from "../../config/dropDownHelpers";
import URDItemForm from "./URDItemForm";
import QuickAddCustomerModal from "../other/QuickAddCustomerModal";
import CustomerAvatarHoverCard from "../other/CustomerAvatarHoverCard";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

// customers
import {
  useGetAllCustomersQuery,
  useLazyGetCustomerByIdQuery,
} from "../../toolkitServices/customerApi";

// URD
import {
  useAddUrdMutation,
  useUpdateUrdMutation,
  useGetLastUrdNumberQuery,
  useDownloadUrdReceiptPdfMutation,
} from "../../toolkitServices/urdApi";

// ✅ PDF open util (inline view, cookie-based)
import ConfirmDialog from "../ConfirmDialog";
import InvoicePrintPreviewModal from "../InvoicePrintPreviewModal";

const CARD_HEIGHT_CLASS = "h-[32vh] sm:h-[32vh]";

/* ---------------------------- react-select styles --------------------------- */
const baseSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "30px",
    height: "30px",
    borderColor: state.isFocused ? "#9481ce" : "#D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(148,129,206,.25)" : "none",
  }),
  menu: (b) => ({ ...b, zIndex: 40, fontSize: "12px" }),
  menuList: (base) => ({ ...base, maxHeight: "180px", overflowY: "auto" }),
  valueContainer: (base) => ({ ...base, padding: "0 6px", height: "30px" }),
  indicatorsContainer: (base) => ({ ...base, height: "30px" }),
  dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
  clearIndicator: (base) => ({ ...base, padding: "4px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
};

const customerSelectStyles = {
  ...baseSelectStyles,
  container: (base) => ({ ...base, width: "100%" }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "calc(100% - 28px)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
  menuList: (base) => ({ ...base, maxHeight: "180px", overflowY: "auto" }),
};

const withErrorBorder = (stylesObj, hasError) => {
  if (!hasError) return stylesObj;
  return {
    ...stylesObj,
    control: (base, state) => {
      const baseCtrl = stylesObj.control
        ? stylesObj.control(base, state)
        : base;
      return {
        ...baseCtrl,
        borderColor: "#ef4444",
        boxShadow: state.isFocused ? "0 0 0 2px rgba(239,68,68,0.20)" : "none",
        "&:hover": { borderColor: "#ef4444" },
      };
    },
  };
};

const inputCls = (hasError) =>
  `w-full px-3 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 ${
    hasError
      ? "border-red-400 focus:ring-red-300"
      : "border-gray-300 focus:ring-violet-400"
  }`;

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const DEFAULTS = {
  customer: null,
  branch: null,
  counter: null,
  urdNo: "",
  urdDate: convertToDateInputFormat(getTodayDate()),
  settlement: "Wallet", // Wallet | Payout
  payoutMode: "",
  payoutAmount: "",
  notes: "",
  urdEntries: [
    {
      category: null,
      product: null,
      qty: "",
      totalWt: "",
      packingWt: "",
      grossWt: "",
      stoneWt: "",
      diamondWt: "",
      netWt: "",
      finePercentage: "",
      fineWt: "",
      ratePerGram: "",
      amount: "",
      urdDescription: "",
    },
  ],
  payments: [],
};

const URDForm = ({
  existingData = null,
  initialData = null,
  mode = "create",
  embedded = false,
  forceWalletSettlement = false,
  onCreated,
  onRequestClose,
  onSubmittingChange,
}) => {
  const navigate = useNavigate();

  // Quick Add Customer
  const [customerSearch, setCustomerSearch] = useState("");
  const [quickOpen, setQuickOpen] = useState(false);
  const lastCreatedRef = useRef(null);
  const [tinyArgs, setTinyArgs] = useState(null);

  const branchesFromState = useSelector((s) => s.branches?.branches);
  const countersFromState = useSelector((s) => s.counters?.counters);
  const authUser = useSelector((s) => s.auth?.user);

  // URD mutations
  const [addUrd, { isLoading: isCreating }] = useAddUrdMutation();
  const [updateUrd, { isLoading: isUpdating }] = useUpdateUrdMutation();
  const [downloadUrdReceiptPdf] = useDownloadUrdReceiptPdfMutation();

  const [urdPreview, setUrdPreview] = useState({
    open: false,
    html: "",
    loading: false,
  });
  const [openConfirmDialog, setConfirmDialogOpen] = useState(false);
  // react-hook-form
  const {
    control,
    handleSubmit,
    register,
    setValue,
    reset,
    formState: { errors },
    getValues,
    watch,
    trigger,
    clearErrors,
    unregister,
  } = useForm({
    shouldUnregister: false,
    defaultValues:
      existingData || {
        ...DEFAULTS,
        ...(initialData || {}),
        settlement: forceWalletSettlement
          ? "Wallet"
          : initialData?.settlement || DEFAULTS.settlement,
      },
  });
  const formData = watch();

  const customer = watch("customer");
  const branch = watch("branch");
  const counter = watch("counter");
  const urdDateVal = watch("urdDate");
  const settlementVal = watch("settlement");
  const isWalletSettlement = settlementVal === "Wallet";

  useEffect(() => {
    if (!forceWalletSettlement) return;
    setValue("settlement", "Wallet");
    setValue("payments", []);
  }, [forceWalletSettlement, setValue]);

  // ✅ Branch change tracking (Invoice jaisa)
  const prevBranchIdRef = useRef(null);

  // ✅ Branch change/clear -> reset customer/counter/payments/search
  useEffect(() => {
    const currentBranchId = branch?.value || null;

    if (prevBranchIdRef.current === null) {
      prevBranchIdRef.current = currentBranchId;
      return;
    }

    if (prevBranchIdRef.current !== currentBranchId) {
      setValue("customer", null);
      setValue("counter", null);
      setValue("payments", []);
      setCustomerSearch("");
    }

    prevBranchIdRef.current = currentBranchId;
  }, [branch?.value, setValue]);

  // -------------------- Customers (BRANCH-WISE) --------------------
  const {
    data: customerResp,
    isFetching: loadingCustomers,
    refetch: refetchCustomers,
  } = useGetAllCustomersQuery(
    {
      page: 1,
      limit: 20,
      sortOrder: "desc",
      sortBy: "createdAt",
      search: customerSearch,
      branchId: branch?.value || undefined, // ✅ branch-wise filter
    },
    {
      skip: !branch?.value, // ✅ branch select hone ke baad hi hit kare
    },
  );

  // Tiny fetch for freshly created customer (branch-wise)
  const { data: tinyResp } = useGetAllCustomersQuery(tinyArgs, {
    skip: !tinyArgs,
  });

  // fetch a single customer on demand (refresh wallet/fine after save)
  const [fetchCustomerById] = useLazyGetCustomerByIdQuery();

  const customers = customerResp?.data || [];

  useEffect(() => {
    let bid = authUser?.employeeDetails?.branch?._id;
    let cid = authUser?.employeeDetails?.counter?._id;
    let bname = authUser?.employeeDetails?.branch?.branchName;
    let cname = authUser?.employeeDetails?.counter?.counterName;
    if (mode != "edit" && authUser?.role == "staff" && bid) {
      setValue("branch", { value: bid, label: bname });
    }
    if (mode != "edit" && authUser?.role == "staff" && cid) {
      setValue("counter", { value: cid, label: cname });
    }
  }, [authUser, mode]);
  useEffect(() => {
    if (!tinyArgs || !tinyResp?.data) return;
    const created = lastCreatedRef.current;
    const newest =
      tinyResp.data.find((c) => String(c?._id) === String(created?._id)) ||
      tinyResp.data[0];
    if (newest) {
      const opt =
        (handleDropDowns({ key: "customer", customers: [newest] }) || [])[0] ||
        null;
      if (opt) onCustomerPicked(opt);
    }
    setTinyArgs(null);
  }, [tinyResp, tinyArgs]);

  // last-number based on branch + date
  const lastNoArgs = useMemo(
    () => ({
      branchId: branch?.value || "",
      date: urdDateVal || "",
    }),
    [branch?.value, urdDateVal],
  );

  const { data: lastNoData, refetch: refetchLastNo } = useGetLastUrdNumberQuery(
    lastNoArgs,
    {
      skip: mode !== "create" || !lastNoArgs.branchId || !lastNoArgs.date,
    },
  );

  const nextUrdNumber = lastNoData?.nextNumber;
  const nextUrdYear = lastNoData?.year;

  const {
    fields: urdFields,
    append: urdAppend,
    remove: urdRemove,
  } = useFieldArray({
    control,
    name: "urdEntries",
  });

  const {
    fields: payFields,
    append: payAppend,
    remove: payRemove,
  } = useFieldArray({ control, name: "payments" });

  // mini-form local state (payout row)
  const [payMode, setPayMode] = useState("Cash");
  const [payAmt, setPayAmt] = useState("");
  const [payRef, setPayRef] = useState("");
  const [payDesc, setPayDesc] = useState("");

  // Customer wallet info
  const walletBalance = customer?.info?.balanceAmount || 0;

  const walletUsed = useMemo(() => {
    let used = 0;
    for (const p of payFields) {
      if (p.mode === "Wallet") used += parseFloat(p?.amount) || 0;
    }
    return +used.toFixed(2);
  }, [payFields]);

  const walletAvailable = Math.max(0, +(walletBalance - walletUsed).toFixed(2));

  // if Wallet selected & no balance → auto switch to Cash
  useEffect(() => {
    if (payMode === "Wallet" && walletAvailable <= 0) setPayMode("Cash");
  }, [walletAvailable, payMode]);

  const paySummary = useMemo(() => {
    let payout = 0;
    for (const p of payFields) payout += parseFloat(p?.amount) || 0;
    return { payout: +payout.toFixed(2) };
  }, [payFields]);

  const addPayment = () => {
    if (isWalletSettlement) {
      toast.error(
        "Settlement is 'Keep in Wallet' – you cannot add payout rows for this URD.",
      );
      return;
    }

    const amt = parseFloat(payAmt);
    if (!Number.isFinite(amt) || amt <= 0) {
      return toast.error("Enter a valid payout amount.");
    }

    if (payMode === "Wallet") {
      if (!customer?.value)
        return toast.error("Select a customer before using Wallet.");
      if (walletAvailable <= 0)
        return toast.error("No wallet balance available.");
      if (amt > walletAvailable) {
        return toast.error(
          `Wallet payout cannot exceed available balance ₹ ${walletAvailable.toFixed(
            2,
          )}.`,
        );
      }
    }

    payAppend({
      type: "Payout",
      mode: payMode,
      amount: amt.toFixed(2),
      refNo: payRef || "",
      description: payDesc || "",
      sourceType: "URD",
    });
    setPayAmt("");
    setPayRef("");
    setPayDesc("");
  };

  // Enrich on pick so hover-card has info even when the option list didn't include it
  const onCustomerPicked = async (picked) => {
    if (!picked) return setValue("customer", null);
    if (picked.info) return setValue("customer", picked);

    try {
      const res = await fetchCustomerById(picked.value).unwrap();
      const fresh = res?.data ?? res;
      const enriched = {
        value: fresh?._id ?? picked.value,
        label:
          fresh?.customerName ||
          fresh?.customerId ||
          fresh?.contact ||
          picked.label,
        info: {
          _id: fresh?._id ?? "",
          customerName: fresh?.customerName ?? "",
          customerId: fresh?.customerId ?? "",
          contact: fresh?.contact ?? "",
          email: fresh?.email ?? "",
          addressLine1: fresh?.addressLine1 ?? "",
          city: fresh?.city ?? "",
          state: fresh?.state ?? "",
          country: fresh?.country ?? "",
          createdAt: fresh?.createdAt ?? "",
          fineGold: fresh?.fineGold ?? 0,
          fineSilver: fresh?.fineSilver ?? 0,
          balanceAmount: fresh?.balanceAmount ?? 0,
        },
      };
      setValue("customer", enriched);
    } catch {
      setValue("customer", picked);
    }
  };

  const urdListRef = useRef(null);
  const rowFirstRefs = useRef({});
  const registerFirstField = (id, el) => {
    if (el) rowFirstRefs.current[id] = el;
  };
  const focusRowAt = (listRef, idx) => {
    const cards = listRef.current?.querySelectorAll(".urd-card");
    if (!cards || !cards.length) return;
    const i = Math.max(0, Math.min(idx, cards.length - 1));
    const card = cards[i];
    card?.scrollIntoView({ behavior: "smooth", block: "start" });
    const id = card?.dataset?.fieldId;
    const el = rowFirstRefs.current[id];
    if (el?.focus) el.focus();
  };

  // Focus helpers
  const customerSelectRef = useRef(null);
  const branchSelectRef = useRef(null);
  const counterSelectRef = useRef(null);

  const focusTop = () => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (embedded) {
          counterSelectRef.current?.focus?.();
          return;
        }
        if (!branch?.value) {
          if (branchSelectRef.current?.focus) branchSelectRef.current.focus();
          return;
        }
        if (customerSelectRef.current?.focus) customerSelectRef.current.focus();
      }, 0);
    });
  };

  useEffect(() => {
    if (!embedded) return;
    const timer = setTimeout(() => counterSelectRef.current?.focus?.(), 100);
    return () => clearTimeout(timer);
  }, [embedded]);

  const doReset = useCallback(() => {
    reset({
      ...DEFAULTS,
      ...(embedded ? initialData || {} : {}),
      settlement: forceWalletSettlement ? "Wallet" : DEFAULTS.settlement,
    });
    setCustomerSearch("");
    focusTop();
  }, [embedded, forceWalletSettlement, initialData, reset]);

  // Load existing data into the form on edit
  useEffect(() => {
    if (mode === "edit" && existingData) reset(existingData);
  }, [mode, existingData, reset]);

  const entries = useWatch({ control, name: "urdEntries" }) || [];

  const summary = useMemo(() => {
    let rows = urdFields.length;
    let qty = 0,
      totalWt = 0,
      packing = 0,
      gross = 0,
      stoneWt = 0,
      diaCt = 0,
      diaGm = 0,
      netWt = 0,
      fineWt = 0,
      amount = 0;

    for (const it of entries) {
      qty += num(it?.qty);
      const tw = num(it?.totalWt);
      const pw = num(it?.packingWt);
      const stw = num(it?.stoneWt);
      const dct = num(it?.diamondWt);
      const dgm = dct * 0.2;

      totalWt += tw;
      packing += pw;
      gross += +(tw - pw);
      stoneWt += stw;
      diaCt += dct;
      diaGm += dgm;

      const nw = +(tw - pw - stw - dgm);
      netWt += nw;
      fineWt += num(it?.fineWt);
      amount += num(it?.amount);
    }

    return {
      rows,
      qty: Math.round(qty),
      totalWt: +totalWt.toFixed(3),
      packingWt: +packing.toFixed(3),
      grossWt: +gross.toFixed(3),
      stoneWt: +stoneWt.toFixed(3),
      diamondCt: +diaCt.toFixed(3),
      diamondGram: +diaGm.toFixed(3),
      netWt: +netWt.toFixed(3),
      fineWt: +fineWt.toFixed(3),
      amount: +amount.toFixed(2),
    };
  }, [entries, urdFields.length]);

  const getFirstErrorRowIndex = () => {
    const se = errors?.urdEntries;
    if (!se) return -1;
    const rows = Object.keys(se)
      .map((k) => Number(k))
      .sort((a, b) => a - b);
    return rows.length ? rows[0] : -1;
  };

  // build counter options filtered by selected branch
  const counterOptions = useMemo(() => {
    const all = Array.isArray(countersFromState) ? countersFromState : [];
    const brId = branch?.value;

    const filtered = brId
      ? all.filter(
          (c) => String(c?.branchId?._id ?? c?.branchId) === String(brId),
        )
      : [];

    const viaHelper =
      handleDropDowns?.({ key: "counter", counters: filtered }) || null;
    if (viaHelper && Array.isArray(viaHelper) && viaHelper.length)
      return viaHelper;

    return filtered.map((c) => ({
      value: c?._id,
      label: c?.counterName || c?.counterCode || "Counter",
      info: c,
    }));
  }, [countersFromState, branch?.value]);

  // If branch is cleared, clear counter & customer
  useEffect(() => {
    if (!branch?.value) {
      if (counter?.value) setValue("counter", null);
      if (customer?.value) setValue("customer", null);
      if ((payFields || []).length) setValue("payments", []);
      setCustomerSearch("");
    }
  }, [
    branch?.value,
    counter?.value,
    customer?.value,
    payFields?.length,
    setValue,
  ]);
  const handleFormSubmit = async (data) => {
    setConfirmDialogOpen(true); // Show confirmation dialog
  };

  const onConfirm = () => {
    setConfirmDialogOpen(false);
    onSubmit();
  };
  const onCancel = () => {
    setConfirmDialogOpen(false);
  };

  const openUrdPreviewModal = async (id) => {
    try {
      setUrdPreview({ open: true, html: "", loading: true });

      const htmlContent = await downloadUrdReceiptPdf(id).unwrap();

      setUrdPreview({
        open: true,
        html: htmlContent,
        loading: false,
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to load URD preview.");
      setUrdPreview({ open: false, html: "", loading: false });
    }
  };

  const closeUrdPreview = () => {
    setUrdPreview({ open: false, html: "", loading: false });
  };
  // ===== SUBMIT (create / update) =====
  const onSubmit = async () => {
    if (!formData?.branch?.value) return toast.error("Select a branch.");
    if (!formData?.customer?.value) return toast.error("Select a customer.");
    if (!formData?.counter?.value) return toast.error("Select a counter.");
    if (!formData?.urdDate) return toast.error("Select a URD date.");
    if (!formData?.urdEntries?.length)
      return toast.error("Add at least one URD item.");

    const urdEntries = formData?.urdEntries?.filter((item) => item?.category);

    const urdTotal = summary.amount || 0;
    const payoutTotal = paySummary.payout || 0;

    if (payoutTotal - urdTotal > 0.01) {
      toast.error(
        `Total payout (₹ ${payoutTotal.toFixed(
          2,
        )}) cannot be more than URD total (₹ ${urdTotal.toFixed(2)}).`,
      );
      return;
    }

    if (formData.settlement === "Payout") {
      if (!formData?.payments?.length) {
        toast.error(
          "Settlement is 'Payout Now' – add at least one payout row.",
        );
        return;
      }
      if (Math.abs(urdTotal - payoutTotal) > 0.01) {
        toast.error(
          `Settlement is 'Payout Now' – total amount is ₹ ${urdTotal.toFixed(
            2,
          )}, but payout amount is ₹ ${payoutTotal.toFixed(
            2,
          )}. Please make payout equal to the total URD amount.`,
        );
        return;
      }
    }

    const { urdNo: _ignoreUrdNo, ...clean } = formData;

    const settlement = forceWalletSettlement
      ? "Wallet"
      : formData.settlement;
    const payments = settlement === "Wallet" ? [] : formData.payments || [];

    const payload = {
      ...clean,
      settlement,
      urdDate: formData.urdDate,
      urdEntries,
      payments,
      branch: formData.branch?.value ? formData.branch : null,
      counter: formData.counter?.value ? formData.counter : null,
      customer: formData.customer?.value ? formData.customer : null,
    };

    onSubmittingChange?.(true);
    try {
      if (mode === "edit" && existingData?._id) {
        const res = await updateUrd({
          id: existingData._id,
          ...payload,
        }).unwrap();
        const updated = res?.data ?? res;
        const id = updated?._id || existingData._id;
        toast.success("URD updated successfully.");

        if (embedded) {
          await onCreated?.(updated, {
            amount: urdTotal,
            previousAmount: Number(existingData?._originalAmount || 0),
            isUpdate: true,
          });
          return;
        }

        if (id && !embedded) await openUrdPreviewModal(id);
        setTimeout(() => navigate(`/purchase/urd-gst-list`), 500);
      } else {
        const res = await addUrd(payload).unwrap();
        const created = res?.data ?? res;
        const id = created?._id;
        toast.success("URD created successfully.");

        if (embedded) {
          await onCreated?.(created, { amount: urdTotal });
          return;
        }

        if (id) await openUrdPreviewModal(id);
        reset({
          ...DEFAULTS,
          branch: formData.branch || null, // ✅ branch same rakho
          counter: null,
          customer: null, // ✅ customer re-pick (branch-wise)
          urdDate: convertToDateInputFormat(getTodayDate()),
        });

        setCustomerSearch("");
        focusTop();
        refetchLastNo();
      }
    } catch (err) {
      const msg =
        err?.data?.message || err?.error || err?.message || "Operation failed.";
      toast.error(msg);
    } finally {
      onSubmittingChange?.(false);
    }
  };

  // Keyboard Shortcuts
  useEffect(() => {
    const onKeys = (e) => {
      const key = (e.key || "").toLowerCase();

      if (
        e.ctrlKey &&
        (e.key === "+" || e.key === "=" || e.code === "NumpadAdd")
      ) {
        e.preventDefault();
        Promise.resolve(trigger()).then((valid) => {
          if (!valid) {
            setTimeout(() => {
              const badIdx = getFirstErrorRowIndex();
              if (badIdx >= 0) focusRowAt(urdListRef, badIdx);
            }, 0);
            return;
          }
          const nextIdx = urdFields.length;
          urdAppend(
            {
              category: null,
              product: null,
              qty: "",
              totalWt: "",
              packingWt: "",
              grossWt: "",
              stoneWt: "",
              diamondWt: "",
              netWt: "",
              finePercentage: "",
              fineWt: "",
              ratePerGram: "",
              amount: "",
              urdDescription: "",
            },
            { shouldFocus: false },
          );
          requestAnimationFrame(() =>
            setTimeout(() => focusRowAt(urdListRef, nextIdx), 0),
          );
        });
        return;
      }

      if (e.ctrlKey && key === "s") {
        e.preventDefault();
        // if (!isCreating && !isUpdating) handleSubmit(onSubmit)();
        setConfirmDialogOpen(true);
        return;
      }

      if (e.ctrlKey && key === "r") {
        e.preventDefault();
        doReset();
        return;
      }

      if (e.ctrlKey && (e.key === "Backspace" || e.code === "Backspace")) {
        e.preventDefault();
        if (embedded) onRequestClose?.();
        else navigate("/purchase/urd-gst-list");
      }
    };

    window.addEventListener("keydown", onKeys);
    return () => window.removeEventListener("keydown", onKeys);
  }, [
    trigger,
    urdFields.length,
    urdAppend,
    handleSubmit,
    onSubmit,
    isCreating,
    isUpdating,
    navigate,
    doReset,
    embedded,
    onRequestClose,
  ]);

  // Add Row button
  const handleAddRow = async () => {
    const valid = await trigger();
    if (!valid) {
      const badIdx = getFirstErrorRowIndex();
      if (badIdx >= 0) focusRowAt(urdListRef, badIdx);
      return;
    }
    const nextIdx = urdFields.length;
    urdAppend(
      {
        category: null,
        product: null,
        qty: "",
        totalWt: "",
        packingWt: "",
        grossWt: "",
        stoneWt: "",
        diamondWt: "",
        netWt: "",
        finePercentage: "",
        fineWt: "",
        ratePerGram: "",
        amount: "",
        urdDescription: "",
      },
      { shouldFocus: false },
    );
    requestAnimationFrame(() =>
      setTimeout(() => focusRowAt(urdListRef, nextIdx), 0),
    );
  };

  // Totals for metrics
  const totalAmount = summary.amount;

  const paidAmount = isWalletSettlement ? totalAmount : paySummary.payout;
  const balanceAmount = +(totalAmount - paidAmount).toFixed(2);

  return (
    <>
      {/* ✅ Quick Add Customer (Branch pass) */}
      <QuickAddCustomerModal
        open={quickOpen}
        onClose={() => setQuickOpen(false)}
        selectedBranch={branch} // ✅ important
        onAdded={async (created) => {
          lastCreatedRef.current = created;

          const justAdded =
            (handleDropDowns({ key: "customer", customers: [created] }) ||
              [])[0] || null;

          if (justAdded) onCustomerPicked(justAdded);

          const searchKey =
            created?.customerId ||
            created?.contact ||
            created?.customerName ||
            "";

          setTinyArgs({
            page: 1,
            limit: 3,
            sortOrder: "desc",
            sortBy: "createdAt",
            search: String(searchKey),
            branchId: branch?.value || undefined, // ✅ branch-wise tiny fetch too
          });

          try {
            await refetchCustomers();
          } catch {}

          setQuickOpen(false);
        }}
      />

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-2">
        {/* Top card */}
        <div className="bg-white shadow-sm rounded-xl px-3 py-2 border">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-8 gap-2">
            {/* Branch */}
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Branch <span className="text-red-500">*</span>
              </label>
              <Controller
                name="branch"
                control={control}
                rules={{ required: "Branch is required" }}
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    ref={branchSelectRef}
                    options={handleDropDowns({
                      key: "branch",
                      branchesFromState,
                      branches: branchesFromState,
                    })}
                    styles={withErrorBorder(
                      baseSelectStyles,
                      !!fieldState.error,
                    )}
                    classNamePrefix="react-select"
                    placeholder="Select branch"
                    isClearable
                    isSearchable
                    isDisabled={embedded}
                  />
                )}
              />
            </div>
            {/* Customer */}
            <div className="min-w-0 sm:col-span-2 lg:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Customer <span className="text-red-500">*</span>
              </label>

              <div className="flex items-center gap-2">
                <div className="w-full">
                  <Controller
                    name="customer"
                    control={control}
                    rules={{ required: "Customer is required" }}
                    render={({
                      field: { value, onChange, onBlur, name },
                      fieldState,
                    }) => (
                      <Select
                        ref={customerSelectRef}
                        inputId="customerSelect"
                        instanceId="customerSelect"
                        name={name}
                        value={value}
                        onChange={(opt) => {
                          onChange(opt ?? null);
                          onBlur?.();
                          onCustomerPicked(opt ?? null);
                        }}
                        onBlur={onBlur}
                        options={handleDropDowns({
                          key: "customer",
                          customers,
                        })}
                        styles={withErrorBorder(
                          customerSelectStyles,
                          !!fieldState.error,
                        )}
                        classNamePrefix="react-select"
                        menuPlacement="bottom"
                        isClearable
                        isDisabled={embedded || !branch?.value} // ✅ branch ke bina disable
                        placeholder={
                          !branch?.value
                            ? "Select branch first"
                            : "Select customer"
                        }
                        onInputChange={(input, meta) => {
                          if (meta.action === "input-change")
                            setCustomerSearch(input.trim());
                        }}
                        isLoading={!!branch?.value && loadingCustomers}
                        filterOption={(candidate, raw) => {
                          if (!raw) return true;
                          const text = (candidate?.label || "")
                            .toString()
                            .toLowerCase();
                          return text.includes(raw.toLowerCase());
                        }}
                      />
                    )}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (!branch?.value) {
                      toast.error("Select branch first.");
                      return;
                    }
                    setQuickOpen(true);
                  }}
                  className="px-4 py-1.5 rounded-md border text-xs text-[#6b59c6] hover:bg-violet-50 focus:outline-none focus:ring-2 focus:ring-[#9481ce]"
                  title="Quick add customer"
                >
                  +
                </button>

                <CustomerAvatarHoverCard
                  size="md"
                  tone="violet"
                  customer={customer}
                  info={{
                    name: customer?.info?.customerName,
                    code: customer?.info?.customerId,
                    phone: customer?.info?.customerId,
                    email: customer?.info?.email,
                    address:
                      `${customer?.info?.addressLine1 || ""} ` +
                      `${customer?.info?.city || ""} ` +
                      `${customer?.info?.state || ""} ` +
                      `${customer?.info?.country || ""}`,
                    since: customer
                      ? formatDate(customer?.info?.createdAt)
                      : "",
                    fineGold: customer?.info?.fineGold,
                    fineSilver: customer?.info?.fineSilver,
                    walletAmount: customer?.info?.balanceAmount,
                    outstandingAmount: customer?.info?.outstandingAmount,
                  }}
                />
              </div>
            </div>

            {/* Counter */}
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Counter <span className="text-red-500">*</span>
              </label>
              <Controller
                name="counter"
                control={control}
                rules={{ required: "Counter is required" }}
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    ref={counterSelectRef}
                    options={counterOptions}
                    isDisabled={!branch?.value}
                    styles={withErrorBorder(
                      baseSelectStyles,
                      !!fieldState.error,
                    )}
                    classNamePrefix="react-select"
                    placeholder={
                      branch?.value ? "Select counter" : "Pick branch first"
                    }
                    isClearable
                    isSearchable
                  />
                )}
              />
            </div>

            {/* Date */}
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                {...register("urdDate", { required: "Date is required" })}
                className={inputCls(!!errors?.urdDate)}
              />
            </div>

            {/* Settlement */}
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Settlement
              </label>
              <select
                {...register("settlement")}
                className={inputCls(false)}
                disabled={forceWalletSettlement}
              >
                <option value="Wallet">Keep in Wallet</option>
                <option value="Payout">Payout Now</option>
              </select>
            </div>
          </div>
        </div>

        {/* Items list */}
        <div className="bg-white shadow-sm rounded-xl p-0 mb-2 z-10">
          <div
            ref={urdListRef}
            className={`relative ${CARD_HEIGHT_CLASS} overflow-y-auto scroll-smooth snap-y snap-mandatory`}
          >
            {urdFields.map((field, idx) => (
              <URDItemForm
                key={field.id}
                control={control}
                register={register}
                unregister={unregister}
                errors={errors}
                rowindex={idx}
                fieldId={field.id}
                watch={watch}
                setValue={setValue}
                getValues={getValues}
                showDelete={true}
                onDelete={() => {
                  const targetIdx = Math.max(0, idx - 1);
                  const rowPath = `urdEntries.${idx}`;
                  try {
                    unregister(rowPath);
                  } catch {}
                  clearErrors(rowPath);
                  urdRemove(idx);
                  requestAnimationFrame(() =>
                    setTimeout(() => focusRowAt(urdListRef, targetIdx), 0),
                  );
                }}
                cardHeightClass={CARD_HEIGHT_CLASS}
                registerFirstField={registerFirstField}
              />
            ))}

            {urdFields.length === 0 && (
              <motion.div
                tabIndex={-1}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0 }}
                className="m-2 p-4 border border-gray-200 hover:border-gray-300 shadow-lg rounded-lg bg-white text-sm text-gray-600"
              >
                No rows. Click “Add Row”.
              </motion.div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Actions */}
          <div className="space-y-2 bg-white rounded-xl px-2 py-2 border">
            {/* mini form */}
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-12 sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 ">
                  Mode
                </label>
                <select
                  value={payMode}
                  onChange={(e) => setPayMode(e.target.value)}
                  disabled={isWalletSettlement}
                  className={`w-full px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                    isWalletSettlement
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : ""
                  }`}
                >
                  <option>Cash</option>
                  <option>UPI</option>
                  <option>Bank</option>
                  {walletBalance > 0 && <option>Wallet</option>}
                </select>
              </div>

              <div className="col-span-6 sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-600">
                  Amount (₹)
                </label>
                <input
                  type="number"
                  value={payAmt}
                  onChange={(e) => setPayAmt(e.target.value)}
                  placeholder="0.00"
                  disabled={isWalletSettlement}
                  className={`w-full px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                    isWalletSettlement
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : ""
                  }`}
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label className="block text-xs font-semibold text-slate-600 ">
                  Ref No
                </label>
                <input
                  type="text"
                  value={payRef}
                  onChange={(e) => setPayRef(e.target.value)}
                  placeholder="Txn/Chq/UPI ID"
                  disabled={isWalletSettlement}
                  className={`w-full px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                    isWalletSettlement
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : ""
                  }`}
                />
              </div>

              <div className="col-span-12 sm:col-span-4">
                <label className="block text-xs font-semibold text-slate-600">
                  Description
                </label>
                <input
                  type="text"
                  value={payDesc}
                  onChange={(e) => setPayDesc(e.target.value)}
                  placeholder="Narration / reason"
                  disabled={isWalletSettlement}
                  className={`w-full px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                    isWalletSettlement
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : ""
                  }`}
                />
              </div>

              <div className="col-span-12 sm:col-span-1">
                <button
                  type="button"
                  onClick={addPayment}
                  disabled={isWalletSettlement}
                  className={`w-full inline-flex items-center justify-center gap-2 py-2 px-2 rounded-md text-sm font-medium shadow-sm ${
                    isWalletSettlement
                      ? "bg-violet-300 text-white cursor-not-allowed"
                      : "bg-violet-600 hover:bg-violet-700 text-white"
                  }`}
                >
                  <FaPlus className="text-xs" />
                </button>
              </div>
            </div>

            {/* payments table */}
            <div className="border rounded-lg overflow-hidden">
              <div className="h-28 overflow-y-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600 sticky top-0 z-10">
                    <tr>
                      <th className="text-left px-2 py-1 border-b">#</th>
                      <th className="text-left px-3 py-2 border-b">Mode</th>
                      <th className="text-right px-3 py-2 border-b">
                        Amount (₹)
                      </th>
                      <th className="text-left px-3 py-2 border-b">Ref No</th>
                      <th className="text-left px-3 py-2 border-b">
                        Description
                      </th>
                      <th className="text-right px-3 py-2 border-b">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payFields.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-3 py-3 text-center text-slate-500"
                        >
                          No payouts added.
                        </td>
                      </tr>
                    ) : (
                      payFields.map((p, i) => (
                        <tr
                          key={p.id}
                          className="odd:bg-white even:bg-slate-50"
                        >
                          <td className="px-3 py-2">{i + 1}</td>
                          <td className="px-3 py-2">{p.mode}</td>
                          <td className="px-3 py-2 text-right">
                            {Number(p.amount).toFixed(2)}
                          </td>
                          <td className="px-3 py-2">{p.refNo || "—"}</td>
                          <td className="px-3 py-2">{p?.description || "—"}</td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => payRemove(i)}
                              className="inline-flex text-center items-center gap-1 text-red-600 hover:text-red-700"
                              title="Remove"
                            >
                              <FaTrash className="text-[12px]" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Metrics */}
          <div className="space-y-2">
            <div className="border rounded-lg px-2 py-2 bg-white">
              <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">ITEMS</span>
                  <span className="font-medium text-blue-600">
                    {summary.rows}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">QTY</span>
                  <span className="font-medium text-blue-600">
                    {summary.qty}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">TOTAL WT</span>
                  <span className="font-medium text-blue-600">
                    {summary.totalWt.toFixed(3)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">GROSS WT</span>
                  <span className="font-medium text-blue-600">
                    {summary.grossWt.toFixed(3)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">STONE WT</span>
                  <span className="font-medium text-blue-600">
                    {summary.stoneWt.toFixed(3)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">
                    DIA WT (ct / g)
                  </span>
                  <span className="font-medium text-blue-600">
                    {summary.diamondCt.toFixed(3)} /{" "}
                    {summary.diamondGram.toFixed(3)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">NET WT</span>
                  <span className="font-medium text-blue-600">
                    {summary.netWt.toFixed(3)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">FINE WT</span>
                  <span className="font-semibold text-blue-600">
                    {summary.fineWt.toFixed(3)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">TOTAL AMOUNT</span>
                  <span className="font-semibold text-blue-600">
                    ₹ {summary.amount.toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">PAID AMOUNT</span>
                  <span className="font-semibold text-emerald-700">
                    ₹ {paidAmount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">BALANCE</span>
                  <span
                    className={`font-semibold ${
                      balanceAmount >= 0 ? "text-blue-700" : "text-red-700"
                    }`}
                    title={balanceAmount >= 0 ? "Balance due" : "Overpaid"}
                  >
                    ₹ {Math.abs(balanceAmount).toFixed(2)}
                    {balanceAmount < 0 ? " (overpaid)" : ""}
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* /Metrics */}
        </div>

        {/* Bottom action bar */}
        <div className="space-y-2 bg-white rounded-xl p-2 border">
          <div className="pt-2 flex flex-wrap gap-2 justify-between items-center">
            <motion.button
              whileHover={{ scale: 1.02, y: -1 }}
              whileTap={{ scale: 0.98, y: 0 }}
              type="button"
              onClick={handleAddRow}
              className="group inline-flex items-center gap-2 rounded-full bg-violet-500 px-3.5 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-violet-600 hover:shadow-md transition-all"
            >
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                <FaPlus className="text-[10px]" />
              </span>
              <span className="tracking-wide">Add Row</span>
              <span className="ml-1 h-4 w-px bg-violet-300/80 group-hover:bg-white/60" />
              <span className="inline-flex items-center rounded-full bg-violet-400/90 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide">
                Ctrl + +
              </span>
            </motion.button>

            <div className="flex gap-2 ml-auto">
              <motion.button
                whileHover={{
                  scale: isCreating || isUpdating ? 1 : 1.02,
                  y: isCreating || isUpdating ? 0 : -1,
                }}
                whileTap={{ scale: isCreating || isUpdating ? 1 : 0.98, y: 0 }}
                type="submit"
                disabled={isCreating || isUpdating}
                className="group inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-emerald-600 hover:shadow-md transition-all disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                  <FaCheck className="text-[10px]" />
                </span>
                <span className="tracking-wide">
                  {isCreating || isUpdating
                    ? mode === "edit"
                      ? "Updating..."
                      : "Saving..."
                    : mode === "edit"
                      ? "Update"
                      : "Save"}
                </span>
                <span className="ml-1 h-4 w-px bg-emerald-300/80 group-hover:bg-white/60" />
                <span className="inline-flex items-center rounded-full bg-emerald-400/90 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide">
                  Ctrl + S
                </span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98, y: 0 }}
                type="button"
                onClick={doReset}
                className="group inline-flex items-center gap-2 rounded-full border border-slate-300 bg-slate-50 px-3.5 py-1.5 text-[11px] font-semibold text-slate-700 shadow-sm hover:bg-slate-100 hover:border-slate-400 hover:shadow-md transition-all"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-700 text-white">
                  <FaUndo className="text-[10px]" />
                </span>
                <span className="tracking-wide">Reset</span>
                <span className="ml-1 h-4 w-px bg-slate-200 group-hover:bg-slate-300" />
                <span className="inline-flex items-center rounded-full bg-white/80 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide text-slate-700">
                  Ctrl + R
                </span>
              </motion.button>

              {!embedded && (
              <motion.button
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98, y: 0 }}
                type="button"
                onClick={() => navigate("/purchase/urd-gst-list")}
                className="group inline-flex items-center gap-2 rounded-full bg-indigo-500 px-3.5 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-indigo-600 hover:shadow-md transition-all"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                  <FaList className="text-[10px]" />
                </span>
                <span className="tracking-wide">URD List</span>
                <span className="ml-1 h-4 w-px bg-indigo-300/80 group-hover:bg-white/60" />
                <span className="inline-flex items-center rounded-full bg-indigo-400/90 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide">
                  Ctrl + ⌫
                </span>
              </motion.button>
              )}
            </div>
          </div>
        </div>
      </form>

      <ConfirmDialog
        open={openConfirmDialog}
        onCancel={onCancel}
        onConfirm={onConfirm}
        title="Confirm URD Creation"
        message="Once the URD is created, it cannot be reverted or can not make entry in future as URD returned. URD entries can only be deleted on the same day as creation. Are you sure you want to proceed?"
        confirmText="Yes, Proceed"
        cancelText="Cancel"
      />
      <InvoicePrintPreviewModal
        open={urdPreview.open}
        html={urdPreview.html}
        loading={urdPreview.loading}
        onClose={closeUrdPreview}
      />
    </>
  );
};

export default URDForm;
